import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function createEmptyForm() {
  return {
    nome: '',
    target_amount: '',
    current_amount: '',
    target_date: '',
    priority: '3',
    notes: '',
  };
}

const PersonalFinanceGoalsPage = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(createEmptyForm());

  const loadGoals = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/goals');
      setGoals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar metas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGoals();
  }, []);

  const saveGoal = async () => {
    try {
      if (!form.nome.trim()) {
        toast.error('Nome da meta e obrigatorio');
        return;
      }
      if (!form.target_amount) {
        toast.error('Valor alvo e obrigatorio');
        return;
      }

      setSaving(true);
      await axios.post('/api/personal-finance/goals', {
        nome: form.nome.trim(),
        target_amount: form.target_amount,
        current_amount: form.current_amount || 0,
        target_date: form.target_date || null,
        priority: Number(form.priority || 3),
        notes: form.notes || null,
      });
      setForm(createEmptyForm());
      toast.success('Meta criada');
      await loadGoals();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar meta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.1fr]">
      <div className="card">
        <div className="card-body space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Meta nova</p>
            <h2 className="text-xl font-semibold text-slate-900">Objetivo financeiro</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label">Nome</label>
              <input className="form-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Reserva de emergencia, viagem, entrada..." />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="form-label">Valor alvo</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Valor atual</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.current_amount} onChange={(e) => setForm({ ...form, current_amount: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="form-label">Data alvo</label>
                <input className="form-input" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Prioridade</label>
                <select className="form-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="1">1 - Critica</option>
                  <option value="2">2 - Alta</option>
                  <option value="3">3 - Media</option>
                  <option value="4">4 - Baixa</option>
                  <option value="5">5 - Opcional</option>
                </select>
              </div>
            </div>
            <div>
              <label className="form-label">Notas</label>
              <textarea className="form-textarea min-h-[120px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contexto da meta, estrategia de aporte, restricoes..." />
            </div>
          </div>

          <button className="btn-primary w-full" onClick={saveGoal} disabled={saving}>
            {saving ? 'Salvando...' : 'Criar meta'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Backlog vivo</p>
              <h2 className="text-xl font-semibold text-slate-900">Metas do household</h2>
            </div>
            <button className="btn-secondary" onClick={loadGoals} disabled={loading}>Atualizar</button>
          </div>

          <div className="space-y-3">
            {loading && <p className="text-sm text-slate-500">Carregando metas...</p>}
            {!loading && goals.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                Nenhuma meta criada ainda. O corte atual entrega a fundacao do dominio de metas.
              </div>
            )}
            {goals.map((goal) => {
              const progress = goal.target_amount > 0
                ? Math.min(100, Math.round((Number(goal.current_amount || 0) / Number(goal.target_amount || 0)) * 100))
                : 0;
              return (
                <div key={goal.id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-lg font-semibold text-slate-900">{goal.nome}</p>
                      <p className="mt-1 text-sm text-slate-500">{goal.notes || 'Sem notas adicionais.'}</p>
                    </div>
                    <span className="badge-info">P{goal.priority}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                    <span>Atual: {currency.format(Number(goal.current_amount || 0))}</span>
                    <span>Alvo: {currency.format(Number(goal.target_amount || 0))}</span>
                    <span>Prazo: {goal.target_date ? new Date(goal.target_date).toLocaleDateString('pt-BR') : 'Nao definido'}</span>
                  </div>
                  <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-emerald-700">{progress}% do alvo</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceGoalsPage;
